import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Helmet from 'react-helmet';
import { getRecords, GameRecords, GameRecord, recordOptions } from '../services/records.service';
import { useTranslation } from '../hooks/useTranslation';
import logo from '../assets/images/logoImg.png';
import classes from './recordsPage.module.scss';

const formatRecordTime = (points: number) => {
  const seconds = Math.floor(Math.abs(points) / 1000);
  return [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
    .map(value => String(value).padStart(2, '0')).join(':');
};

export default function RecordsPage() {
  const [params, setParams] = useSearchParams();
  const selected = recordOptions.find(option => option.key === params.get('gameKey')) || recordOptions[0];
  const [records, setRecords] = useState<GameRecords>();
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const { getTranslation, languageRef } = useTranslation();
  const t = getTranslation('records');
  useEffect(() => {
    const controller = new AbortController();
    setRecords(undefined); setError(false);
    getRecords(selected.key, controller.signal).then(data => {
      if (!controller.signal.aborted) setRecords(data);
    }).catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [selected.key, retry]);
  const names: Record<string, string> = {
    bubblewar: getTranslation('gamekeysbubblewar').name,
    rivercirclecoop: t.riverCircle,
    roperage: getTranslation('gamekeysroperage').name,
  };
  const label = (option: typeof selected) => names[option.game] + (option.players ? ' · ' + (option.players === 1 ? t.onePlayer : t.players).replace('{{count}}', String(option.players)) : '');
  const recordName = (entry: GameRecord) => [entry.teamName, entry.name].find(name => typeof name === 'string' && name.trim()) || t.anonymous;
  const table = (heading: string, entries: GameRecord[]) => <section className={classes.board}>
    <h2>{heading}</h2>
    {entries.length ? <div className={classes.tableScroll}><table>
      <thead><tr><th scope="col">{t.rank}</th><th scope="col">{t.name}</th><th scope="col">{t.country}</th>{selected.game === 'bubblewar' && <th scope="col">{t.levelReached}</th>}<th scope="col">{selected.game === 'rivercirclecoop' ? t.time : t.score}</th></tr></thead>
      <tbody>{entries.map((entry, index) => <tr key={index}>
        <td>{index + 1}</td><td>{recordName(entry)}</td>
        <td>{entry.country && entry.country !== 'Unknown' ? entry.country : '—'}</td>
        {selected.game === 'bubblewar' && <td>{typeof entry.levelReached === 'number' && Number.isFinite(entry.levelReached) ? entry.levelReached : '—'}</td>}
        <td>{selected.game === 'rivercirclecoop' ? formatRecordTime(entry.points) : entry.points.toLocaleString()}</td>
      </tr>)}</tbody>
    </table></div> : <p className={classes.message}>{t.empty}</p>}
  </section>;
  return <main className={classes.page} dir={['he', 'ar'].includes(languageRef.current) ? 'rtl' : 'ltr'}>
    <Helmet><title>{t.title} | PlayCloud</title></Helmet>
    <a className={classes.brand} href="/"><img src={logo} alt="" />PlayCloud</a>
    <header className={classes.header}><h1>{t.title}</h1><p>{t.subtitle}</p></header>
    <div className={classes.options} role="group" aria-label={t.selectGame}>
      {recordOptions.map(option => <button key={option.key} type="button" aria-pressed={selected.key === option.key}
        onClick={() => { const next = new URLSearchParams(params); next.set('gameKey', option.key); setParams(next); }}>{label(option)}</button>)}
    </div>
    <h2 className={classes.selection}>{label(selected)}</h2>
    {!records && !error && <p className={classes.message} role="status">{t.loading}</p>}
    {error && <div className={classes.message} role="alert"><p>{t.error}</p><button type="button" onClick={() => setRetry(value => value + 1)}>{t.retry}</button></div>}
    {records && <div className={classes.boards}>{table(t.allTime, records.alltime)}{table(t.monthly, records.monthly)}</div>}
  </main>;
}
